import { useEffect, useState } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import axios from "axios";
import { API_URL } from "./apiConfig";
import { isPlatformHost, platformOrigin } from "./customDomain";
import Catalog from "./pages/catalog";

function RedirectToPlatform({ path = "/" }) {
  useEffect(() => {
    const target = `${platformOrigin()}${path.startsWith("/") ? path : `/${path}`}`;
    window.location.replace(target);
  }, [path]);
  return (
    <div style={{ minHeight: "40vh", display: "grid", placeItems: "center", fontFamily: "system-ui" }}>
      Redirecting…
    </div>
  );
}

function CustomDomainCatalog({ catalogKey, menuOnly = false }) {
  return (
    <Catalog
      forcedStoreKey={catalogKey}
      customDomainMode
      menuOnly={menuOnly}
    />
  );
}

export function buildCustomDomainRouter(catalogKey) {
  const key = String(catalogKey || "").trim();
  return createBrowserRouter(
    [
      { path: "/", element: <CustomDomainCatalog catalogKey={key} /> },
      { path: "/menu", element: <CustomDomainCatalog catalogKey={key} menuOnly /> },
      {
        path: "/menu/:productId",
        element: <CustomDomainCatalog catalogKey={key} menuOnly />,
      },
      {
        path: "/catalog/:storeKey/*",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/catalog/:storeKey",
        element: <Navigate to="/" replace />,
      },
      { path: "/login", element: <RedirectToPlatform path="/login" /> },
      { path: "/register", element: <RedirectToPlatform path="/register" /> },
      { path: "/dashboard", element: <RedirectToPlatform path="/dashboard" /> },
      { path: "/settings", element: <RedirectToPlatform path="/settings" /> },
      { path: "/admin/*", element: <RedirectToPlatform path="/admin" /> },
      { path: "/:productId", element: <CustomDomainCatalog catalogKey={key} /> },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
    {
      future: {
        v7_startTransition: true,
      },
    }
  );
}

/**
 * Resolves whether the current hostname is a verified merchant custom domain.
 * Returns { status: 'platform' | 'loading' | 'matched' | 'unknown', catalogKey? }.
 */
/**
 * The server already resolved this host while rendering the shell (it only injects window.__STORE__
 * for a host that maps to an active store), so the resolve-domain round-trip and the "Loading…"
 * screen can be skipped. Returns null when there is no such seed.
 */
function seededCustomDomainState() {
  try {
    const seed = window.__STORE__;
    const key = seed && typeof seed === "object" ? String(seed.catalog_key || "").trim() : "";
    if (!key || !Array.isArray(seed.keys)) return null;
    return { status: "matched", catalogKey: key, storeName: String(seed.name || "") };
  } catch {
    return null;
  }
}

export function useCustomDomainResolution() {
  const [state, setState] = useState(() => {
    if (typeof window === "undefined") return { status: "platform" };
    if (isPlatformHost(window.location.hostname)) return { status: "platform" };
    return seededCustomDomainState() || { status: "loading" };
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const host = window.location.hostname;
    if (isPlatformHost(host)) {
      setState({ status: "platform" });
      return undefined;
    }
    if (seededCustomDomainState()) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/public/resolve-domain`, {
          params: { host },
          timeout: 12000,
        });
        if (cancelled) return;
        if (res?.data?.matched && res.data.catalog_key) {
          setState({
            status: "matched",
            catalogKey: String(res.data.catalog_key).trim(),
            storeName: res.data.store_name || "",
          });
        } else {
          setState({ status: "unknown" });
        }
      } catch {
        if (!cancelled) setState({ status: "unknown" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

export function CustomDomainUnknown() {
  const app = platformOrigin();
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        fontFamily: "system-ui, sans-serif",
        background: "#f8fafc",
        color: "#0f172a",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 420 }}>
        <h1 style={{ fontSize: 22, marginBottom: 8 }}>Domain not connected</h1>
        <p style={{ color: "#64748b", lineHeight: 1.5, marginBottom: 20 }}>
          This domain is not linked to an active Kataloji catalog yet. Finish DNS setup in
          Settings, or open your catalog on Kataloji.
        </p>
        <a
          href={app}
          style={{
            display: "inline-block",
            padding: "10px 16px",
            borderRadius: 8,
            background: "#0f172a",
            color: "#fff",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          Go to Kataloji
        </a>
      </div>
    </div>
  );
}

export function CustomDomainRouter({ catalogKey }) {
  const [router] = useState(() => buildCustomDomainRouter(catalogKey));
  return <RouterProvider router={router} />;
}
